import { queryClient } from '@/router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useDialog } from '@/lib/hooks/use-dialog'
import { deleteProcessingTimeDataMutation } from '@/lib/queries/business/v1/case-extra/processing-time/mutations'
import { getCaseExtraInfoQuery, getCaseExtraInfoQueryKey } from '@/lib/queries/business/v3/case-extra/queries'
import { doPolling } from '@/lib/utils/function'

import { useFindTargetCase } from '../../../hooks/use-find-target-case'
import { useProcessingTimeTrendDialogStore } from '../../stores/use-processing-time-trend-dialog-store'
import { useUpdateCaseInfoDialogStore } from '../../stores/use-update-case-info-dialog-store'

export const useCaseProcessingTimeInfo = () => {
  const { targetCase } = useFindTargetCase()

  const { openDialog: openUpdateCaseInfoDialog } = useUpdateCaseInfoDialogStore()
  const { openDialog: openProcessingTimeTrendDialog } = useProcessingTimeTrendDialogStore()

  const { data: dataCaseExtraInfo } = useQuery(getCaseExtraInfoQuery(targetCase?.case_id))
  const { mutateAsync: deleteProcessingTimeData, isPending: isDeletingProcessingTimeData } = useMutation(
    deleteProcessingTimeDataMutation
  )

  const {
    isDialogOpen: isOpenProcessingTimeDataDeleteConfirmDialog,
    openDialog: openProcessingTimeDataDeleteConfirmDialog,
    closeDialog: closeProcessingTimeDataDeleteConfirmDialog,
  } = useDialog()

  const handleGetProcessingTimeButton = () => {
    openUpdateCaseInfoDialog()
  }

  const handleClickDeleteConfirmButton = async () => {
    const caseId = targetCase?.case_id
    if (!caseId) {
      return
    }

    await deleteProcessingTimeData(
      {
        caseId,
      },
      {
        onSuccess: () => {
          toast.success('Processing time data has been successfully deleted!', {
            duration: 5000,
          })
          void queryClient.invalidateQueries({
            queryKey: getCaseExtraInfoQueryKey(caseId),
          })
          doPolling({
            fn: () => {
              void queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.business.v4.case.search.index],
              })
            },
            times: 4,
            interval: 2000,
          })
        },
        onError: () => {
          toast.error('Failed to delete processing time data', {
            duration: 5000,
          })
        },
        onSettled: () => {
          closeProcessingTimeDataDeleteConfirmDialog()
        },
      }
    )
  }

  // Data
  const caseId = dataCaseExtraInfo?.caseId
  const statusIcon = dataCaseExtraInfo?.caseInquiry?.inquiryIcon
  const statusText = dataCaseExtraInfo?.caseInquiry?.inquiryText

  const {
    isAvailableRequest,
    estimatedTime,
    unit: estimatedTimeUnit,
  } = dataCaseExtraInfo?.caseInquiry?.processingTime ?? {}

  const hasProcessingTimeData = !!(
    dataCaseExtraInfo?.categoryCode &&
    dataCaseExtraInfo?.officeCode &&
    dataCaseExtraInfo?.applicationFiledDate
  )
  const showsSubmitCaseInquiry =
    typeof caseId !== 'undefined' &&
    !targetCase?.is_end &&
    !!(isAvailableRequest || dataCaseExtraInfo?.inquirySubmitted)

  return {
    caseId,
    statusIcon,
    statusText,
    estimatedTime,
    estimatedTimeUnit,
    dataCaseExtraInfo,
    hasProcessingTimeData,
    showsSubmitCaseInquiry,
    isDeletingProcessingTimeData,
    isOpenProcessingTimeDataDeleteConfirmDialog,
    openUpdateCaseInfoDialog,
    openProcessingTimeTrendDialog,
    openProcessingTimeDataDeleteConfirmDialog,
    closeProcessingTimeDataDeleteConfirmDialog,
    handleGetProcessingTimeButton,
    handleClickDeleteConfirmButton,
  }
}
